import React from 'react';

interface PlatformLogoProps {
  platformName: string;
  size?: 'sm' | 'md' | 'lg';
  logoBase64?: string; // Base64 encoded logo image
}

const PlatformLogo: React.FC<PlatformLogoProps> = ({ platformName, size = 'md', logoBase64 }) => {
  const name = platformName.toLowerCase();
  const sizeMap = {
    sm: { container: 'w-6 h-6', icon: 'w-4 h-4' },
    md: { container: 'w-8 h-8', icon: 'w-5 h-5' },
    lg: { container: 'w-10 h-10', icon: 'w-6 h-6' },
  };
  
  const { container, icon } = sizeMap[size];
  
  // Helper function to create base64 image data URI if not already formatted
  const getImageSrc = (logoData: string) => {
    // If already a complete data URI, return as-is
    if (logoData.startsWith('data:image/')) {
      return logoData;
    }
    // If just base64 data, assume it's PNG and add data URI prefix
    return `data:image/png;base64,${logoData}`;
  };
  
  // Return appropriate logo based on platform name
  const getLogo = () => {
    // Priority 1: Use base64 logo if available
    if (logoBase64 && logoBase64.trim()) {
      // console.log(`Using base64 logo for platform: ${platformName}`, {
      //   logoLength: logoBase64.length,
      //   logoPreview: logoBase64.substring(0, 50) + '...'
      // });
      return (
        <div className={`flex items-center justify-center ${container} rounded-full overflow-hidden bg-gray-100 dark:bg-gray-800`}>
          <img
            src={getImageSrc(logoBase64)}
            alt={`${platformName} logo`}
            className={`${container} object-contain rounded-full`}
            onLoad={() => {
              // console.log(`Successfully loaded base64 logo for platform: ${platformName}`);
            }}
            onError={() => {
              console.warn(`Failed to load base64 logo for platform: ${platformName}`, {
                logoLength: logoBase64.length,
                logoPreview: logoBase64.substring(0, 50) + '...',
                imageSrc: getImageSrc(logoBase64).substring(0, 100) + '...'
              });
              // On error, the component will re-render without the logo prop
            }}
          />
        </div>
      );
    }
    
    // console.log(`No base64 logo available for platform: ${platformName}, using hardcoded logo`);
    // Priority 2: Fallback to hardcoded SVG logos
    return getHardcodedLogo();
  };
  
  const getHardcodedLogo = () => {
    if (name.includes('dialpad')) {
      return (
        <div className={`flex items-center justify-center ${container} rounded-full bg-[#6E1EFF]/10`}>
          <svg viewBox="0 0 400 400" width="16" height="16">
            <path fill="#6E1EFF" d="M200 0C89.5 0 0 89.5 0 200s89.5 200 200 200 200-89.5 200-200S310.5 0 200 0zm-70 280c-38.1 0-70-31.9-70-70s31.9-70 70-70 70 31.9 70 70-31.9 70-70 70zm0-210c-38.1 0-70-31.9-70-70s31.9-70 70-70 70 31.9 70 70-31.9 70-70 70zm140 70c-38.1 0-70-31.9-70-70s31.9-70 70-70 70 31.9 70 70-31.9 70-70 70zm0 140c-38.1 0-70-31.9-70-70s31.9-70 70-70 70 31.9 70 70-31.9 70-70 70z" />
          </svg>
        </div>
      );
    } else if (name.includes('zoom')) {
      return (
        <div className={`flex items-center justify-center ${container} rounded-full bg-[#2D8CFF]/10`}>
          <svg viewBox="0 0 1024 1024" width="16" height="16">
            <path fill="#2D8CFF" d="M512 0C229.232 0 0 229.232 0 512c0 282.784 229.232 512 512 512 282.784 0 512-229.216 512-512C1024 229.232 794.784 0 512 0zm0 972.8C256.848 972.8 51.2 767.152 51.2 512 51.2 256.848 256.848 51.2 512 51.2c255.152 0 460.8 205.648 460.8 460.8 0 255.152-205.648 460.8-460.8 460.8zm204.8-358.4h-153.6V307.2c0-14.16-11.44-25.6-25.6-25.6-14.144 0-25.6 11.44-25.6 25.6v332.8c0 14.144 11.456 25.6 25.6 25.6h179.2c14.144 0 25.6-11.456 25.6-25.6 0-14.144-11.456-25.6-25.6-25.6z" />
          </svg>
        </div>
      );
    } else if (name.includes('slack')) {
      return (
        <div className={`flex items-center justify-center ${container} rounded-full bg-[#4A154B]/10`}>
          <svg viewBox="0 0 24 24" width="16" height="16">
            <path fill="#4A154B" d="M9.879 10.995l1.035 3.085 3.205-1.074-1.035-3.074-3.205 1.08v-.017z" />
            <path fill="#4A154B" d="M18.824 14.055l-1.555.521.54 1.61c.218.65-.135 1.355-.786 1.574-.15.045-.285.067-.435.063-.511-.015-.976-.338-1.155-.849l-.54-1.607-3.21 1.073.539 1.608c.211.652-.135 1.358-.794 1.575-.15.048-.285.067-.435.064-.51-.015-.976-.34-1.156-.85l-.539-1.619-1.561.524c-.15.045-.29.075-.435.063-.435-.015-.856-.225-1.095-.621-.3-.39-.36-.93-.15-1.386l.54-1.619-3.19-1.076-.54 1.619c-.21.654-.78.99-1.43.78-.65-.21-.99-.779-.78-1.428l.539-1.619-1.56-.525c-.648-.218-.99-.78-.781-1.428.21-.648.779-.99 1.428-.78l1.56.525L7.5 8.75l-1.551-.52c-.646-.215-.989-.779-.774-1.425.217-.652.779-.987 1.428-.775l1.551.525.54-1.605c.211-.651.779-.99 1.428-.78.646.21.989.779.774 1.425l-.539 1.61L13.6 6.12l-.54-1.605c-.209-.645.135-1.35.779-1.574.645-.209 1.344.135 1.565.78l.54 1.604 1.555-.51c.651-.219 1.356.135 1.575.779.218.654-.135 1.359-.784 1.575l-1.557.524 1.035 3.086 1.551-.516c.652-.211 1.358.135 1.575.779.22.649-.135 1.355-.779 1.574l-.011-.029zm4.171-5.356C20.52.456 16.946-1.471 8.699 1.005.456 3.479-1.471 7.051 1.005 15.301c2.475 8.245 6.046 10.17 14.296 7.694 8.245-2.475 10.17-6.046 7.694-14.296z" />
          </svg>
        </div>
      );
    } else {
      // Default logo for other platforms
      return (
        <div className={`flex justify-center items-center bg-gray-200 rounded-full ${container} dark:bg-gray-700`}>
          <svg className={`text-gray-500 ${icon} dark:text-gray-400`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
            <line x1="8" y1="21" x2="16" y2="21"></line>
            <line x1="12" y1="17" x2="12" y2="21"></line>
          </svg>
        </div>
      );
    }
  };

  return getLogo();
};

export default PlatformLogo;